/**
 * MSapling Telemetry Client
 * (Refactored - April 2026 - API Unified)
 */

import { resolveApiBase } from './routing';
import { LocalStorageKey } from './stores/globalStore';

const API_BASE: string = resolveApiBase();

const isTestTelemetry = (): boolean => {
  if (typeof process === 'undefined') return false;
  return String(process.env.VITE_TEST_MODE || process.env.REACT_APP_TEST_MODE || '').toLowerCase() === 'true';
};

// --- Trace Management ---
export const generateTraceId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
};

export const _sessionTraceId: string = generateTraceId();
export const getSessionTraceId = (): string => _sessionTraceId;

export interface FrontendEvent {
  event_name: string;
  component?: string;
  action?: string;
  chat_id?: string;
  project_id?: string;
  trace_id: string;
  timestamp_utc: string;
  user_id_hash?: string;
  test_source: boolean;
}

const getStoredValue = (key: string): string => {
  if (typeof window === 'undefined') return '';
  try {
    return (window.localStorage.getItem(key) || window.sessionStorage.getItem(key) || '').trim();
  } catch {
    return '';
  }
};

const getTelemetryCorrelation = (): Record<string, unknown> => ({
  client_session_id: getStoredValue(LocalStorageKey.SESSION_ID),
  trace_id: getSessionTraceId(),
  request_id: generateTraceId(),
  chat_id: getStoredValue('active_chat_id') || getStoredValue('current_chat_id'),
  project_id: getStoredValue('active_project_id') || getStoredValue('current_project_id'),
  browser_run_id: getStoredValue('browser_extension_browser_run_id'),
  surface: 'web',
  client_type: 'web',
});

// --- Opt-Out Management ---
export const readPersistedOptOut = (): boolean => {
  if (typeof window === 'undefined') return false;
  try { return window.localStorage.getItem(LocalStorageKey.TELEMETRY_OPT_OUT) === '1'; }
  catch { return false; }
};

let _telemetryOptedOut: boolean = readPersistedOptOut();
export const setTelemetryOptOut = (optedOut: boolean): void => {
  _telemetryOptedOut = optedOut;
  if (typeof window !== 'undefined') {
    try {
      if (optedOut) window.localStorage.setItem(LocalStorageKey.TELEMETRY_OPT_OUT, '1');
      else window.localStorage.removeItem(LocalStorageKey.TELEMETRY_OPT_OUT);
    } catch {}
  }
};

// --- Event Queueing ---
export interface TelemetryEvent {
  event: string;
  ts?: string;
  category?: string;
  action?: string;
  label?: string;
  path?: string;
  status?: number;
  duration_ms?: number;
  metadata?: Record<string, unknown>;
  [key: string]: unknown;
}

let queue: TelemetryEvent[] = [];
const MAX_BATCH = 50;
const MAX_QUEUE = 500;
const FLUSH_DELAY_MS = 2000;
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let flushPromise: Promise<void> | null = null;
let flushNotBefore = 0;

const telemetryRetryAt = (error: unknown): number | null => {
  const response = (error as { response?: { status?: number; headers?: any } })?.response;
  if (response?.status !== 429) return null;
  const value = String(response.headers?.get?.('retry-after') ?? response.headers?.['retry-after'] ?? '').trim();
  const seconds = value ? Number(value) : NaN;
  const delay = Number.isFinite(seconds) ? seconds * 1000 : Date.parse(value) - Date.now();
  return Date.now() + Math.max(30_000, Number.isFinite(delay) ? delay : 60_000);
};

export const logTelemetryEvent = (event: TelemetryEvent): void => {
  if (!event || !event.event || _telemetryOptedOut) return;
  queue.push({
    ts: new Date().toISOString(),
    ...event,
  });
  if (queue.length > MAX_QUEUE) queue.splice(0, queue.length - MAX_QUEUE);
  if (queue.length >= MAX_BATCH) flushQueue();
  else scheduleFlush();
};

const flushQueue = (): Promise<void> => {
  if (flushPromise) return flushPromise;
  if (Date.now() < flushNotBefore) {
    scheduleFlush();
    return Promise.resolve();
  }
  const flushing = (async (): Promise<void> => {
    while (queue.length) {
      const batch = queue.splice(0, MAX_BATCH);
      // Use the standard apiClient to avoid redundant CSRF/Auth logic. A failed
      // batch is dropped; telemetry never retries auth or payload failures.
      try {
        const { default: apiClient } = await import('./api/client');
        await apiClient.post('/telemetry/events', { events: batch }, {
          headers: { 'x-ms-telemetry-skip': '1' },
        });
      } catch (err) {
        const retryAt = telemetryRetryAt(err);
        if (retryAt !== null) {
          flushNotBefore = retryAt;
          break;
        }
        console.warn('[Telemetry] Flush failed');
      }
    }
  })();
  flushPromise = flushing;
  void flushing.finally(() => {
    if (flushPromise === flushing) flushPromise = null;
    if (queue.length) scheduleFlush();
  });
  return flushing;
};

const flushQueueForPageHide = (): void => {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (Date.now() < flushNotBefore) {
    queue = [];
    return;
  }
  if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') {
    void flushQueue();
    return;
  }

  // A normal API batch may already be in flight. Beacon only the events still
  // queued, so the same event is never dispatched by both transports.
  while (queue.length) {
    const batch = queue.splice(0, MAX_BATCH);
    const payload = new Blob([JSON.stringify({ events: batch })], { type: 'application/json' });
    try {
      if (!navigator.sendBeacon(`${API_BASE}/api/telemetry/events`, payload)) break;
    } catch {
      break;
    }
  }
  // If the browser rejects a beacon, drop that batch. Do not retry it through
  // another transport after the page begins unloading.
  queue = [];
};

if (typeof window !== 'undefined') window.addEventListener('pagehide', flushQueueForPageHide);

const scheduleFlush = (): void => {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flushQueue();
  }, Math.min(2_147_483_647, Math.max(FLUSH_DELAY_MS, flushNotBefore - Date.now())));
};

// --- Standard Logging Wrappers ---
interface ApiEventProps {
  path: string;
  method: string;
  status?: number;
  durationMs?: number;
  ok?: boolean;
  error?: string;
}

/**
 * POST a client-side error to the dedicated /api/telemetry/client-errors ingest endpoint.
 * Uses keepalive: true so reports survive navigation away from the page.
 */
export const postClientError = (error: { message?: string; stack?: string; [key: string]: unknown }): void => {
  if (_telemetryOptedOut) return;
  const kind = (error['kind'] as string) || 'console_error';
  const message = String(error.message || 'Unknown error').slice(0, 2048);
  const correlation = getTelemetryCorrelation();
  const payload = JSON.stringify({
    kind,
    message,
    stack: error.stack,
    correlation_id: String(correlation.trace_id || ''),
    metadata: {
      ...error,
      correlation,
    },
  });
  try {
    fetch(`${API_BASE}/api/telemetry/client-errors`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: payload,
      keepalive: true,
    }).catch(() => {});
  } catch {
    // best-effort: never throw from telemetry
  }
};

// Web-vital dedup throttle: suppress repeated reports for the same metric within the window.
const WEB_VITAL_THROTTLE_MS = 5_000;
const _webvitalThrottleMap = new Map<string, number>();

/**
 * Send a web vital through the browser unload-safe beacon transport.
 * Falls back to fetch with keepalive: true for reliability during page unload.
 */
export const postWebVital = (name: string, value: number, id: string, path: string): void => {
  if (_telemetryOptedOut) return;
  // Throttle: skip if same metric was reported within WEB_VITAL_THROTTLE_MS
  const throttleKey = `${name}:${id}`;
  const lastSent = _webvitalThrottleMap.get(throttleKey) ?? 0;
  if (Date.now() - lastSent < WEB_VITAL_THROTTLE_MS) return;
  _webvitalThrottleMap.set(throttleKey, Date.now());
  const payload = JSON.stringify({ name, value, id, path, metadata: { correlation: getTelemetryCorrelation() } });
  // Use REACT_APP_API_URL from env if available; fall back to the resolved API_BASE.
  const baseUrl: string = (typeof process !== 'undefined' && process.env?.REACT_APP_API_URL) || API_BASE;
  const url = `${baseUrl}/api/telemetry/vitals`;
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      navigator.sendBeacon(url, new Blob([payload], { type: 'application/json' }));
      return;
    }
  } catch {
    // fall through to fetch
  }
  // Fallback: keepalive fetch survives page unload
  fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: payload,
    keepalive: true,
  }).catch(() => {});
};

export const emitFrontendEvent = (event: { event_name: string; [key: string]: unknown }): void => {
  const frontendEvent: FrontendEvent = {
    event_name: event.event_name,
    component: event.component as string | undefined,
    action: event.action as string | undefined,
    chat_id: event.chat_id as string | undefined,
    project_id: event.project_id as string | undefined,
    trace_id: getSessionTraceId(),
    timestamp_utc: new Date().toISOString(),
    user_id_hash: event.user_id_hash as string | undefined,
    test_source: isTestTelemetry(),
  };
  const { event_name, ...metadata } = frontendEvent;
  logTelemetryEvent({ event: event_name, category: 'frontend', metadata });
};

export const logApiEvent = (props: ApiEventProps): void => {
  // Observing our own ingest response otherwise creates an endless flush loop,
  // including while the user is idle or the ingest endpoint is throttled.
  const path = props.path.replace(/^https?:\/\/[^/]+/i, '').split(/[?#]/, 1)[0];
  if (/^\/(?:api\/)?telemetry(?:\/|$)/.test(path)) return;
  const correlation = getTelemetryCorrelation();
  logTelemetryEvent({
    event: 'api_request',
    category: 'api',
    action: props.method,
    path: props.path,
    status: props.status,
    duration_ms: props.durationMs,
    metadata: { ok: props.ok, error: props.error, correlation },
  });
};

// ============================================================================
// Ad conversion tracking (GA4 / Google Ads / Reddit Pixel)
// ============================================================================
//
// Ad-readiness scaffolding — August 2026. Follows the EXACT gating pattern
// used for Sentry in `frontend/src/index.tsx`: each loader is gated entirely
// on its own env var and is a COMPLETE no-op (no script tag injected, no
// network call, no console error) when that var is unset. This lets the
// operator flip tracking on later by setting an env var at build time —
// never by touching code.
//
// Real GA4 / Google Ads IDs are not yet available (separate account setup in
// progress). A real Reddit Pixel ID (`a2_ioawm5d4plrd`) DOES exist already,
// but it was issued for a different product/domain (play.mtreeai.com) — it
// must NOT be hardcoded here, since using it on msapling.com would
// misattribute conversions to the wrong product. This module never bakes in
// any pixel/measurement ID; it only reads them from env vars.

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    rdt?: ((...args: unknown[]) => void) & { callQueue?: unknown[] };
  }
}

/** True once a given external <script src=...> tag has already been injected. */
const isScriptAlreadyLoaded = (src: string): boolean => {
  if (typeof document === 'undefined') return false;
  return Boolean(document.querySelector(`script[src="${src}"]`));
};

const injectScript = (src: string, async = true): void => {
  if (typeof document === 'undefined' || isScriptAlreadyLoaded(src)) return;
  const script = document.createElement('script');
  script.src = src;
  script.async = async;
  document.head.appendChild(script);
};

/**
 * GA4 (gtag.js) loader. Gated on REACT_APP_GA4_MEASUREMENT_ID. A complete
 * no-op — no script injected, no dataLayer/gtag created — when unset.
 */
export const initGA4 = (): void => {
  const measurementId = process.env.REACT_APP_GA4_MEASUREMENT_ID;
  if (!measurementId || typeof window === 'undefined') return;

  window.dataLayer = window.dataLayer || [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  window.gtag = window.gtag || function gtag(...args: unknown[]) {
    (window.dataLayer as unknown[]).push(args);
  };
  window.gtag('js', new Date());
  window.gtag('config', measurementId);

  injectScript(`https://www.googletagmanager.com/gtag/js?id=${measurementId}`);
};

/**
 * Google Ads conversion tag loader. Gated on REACT_APP_GOOGLE_ADS_ID
 * (e.g. "AW-XXXXXXXXX"). A complete no-op when unset. Shares the same
 * gtag.js loader/dataLayer as GA4 (Google's recommended pattern), but is
 * independently gated so either can be turned on without the other.
 */
export const initGoogleAds = (): void => {
  const adsId = process.env.REACT_APP_GOOGLE_ADS_ID;
  if (!adsId || typeof window === 'undefined') return;

  window.dataLayer = window.dataLayer || [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  window.gtag = window.gtag || function gtag(...args: unknown[]) {
    (window.dataLayer as unknown[]).push(args);
  };
  window.gtag('js', new Date());
  window.gtag('config', adsId);

  injectScript(`https://www.googletagmanager.com/gtag/js?id=${adsId}`);
};

/**
 * Durable server-side backup record for an ad-conversion event. POSTs to
 * /api/telemetry/conversion (CSRF-exempt, see backend/middleware/http_pipeline.py)
 * using the same sendBeacon-first / keepalive-fetch-fallback transport as
 * postWebVital, so the record survives page unload and ad-blockers that
 * strip the client-side gtag()/rdt() calls (which never touch our own
 * backend, so ad-blockers can't intercept this call the same way).
 *
 * This is fire-and-forget by design — a conversion that fails to durably
 * record should never block or throw inside the caller's flow.
 */
const postAdConversionRecord = (
  conversionType: string,
  platform: 'ga4' | 'google_ads' | 'reddit',
  firedClientSide: boolean,
  metadata?: Record<string, unknown>,
): void => {
  try {
    const eventId = generateTraceId();
    const payload = JSON.stringify({
      conversion_type: conversionType,
      platform,
      event_id: eventId,
      fired_client_side: firedClientSide,
      metadata: { ...metadata, correlation: getTelemetryCorrelation() },
    });
    const baseUrl: string = (typeof process !== 'undefined' && process.env?.REACT_APP_API_URL) || API_BASE;
    const url = `${baseUrl}/api/telemetry/conversion`;
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const sent = navigator.sendBeacon(url, new Blob([payload], { type: 'application/json' }));
      if (sent) return;
      // sendBeacon returns false if it declines to queue the request (e.g.
      // payload too large, or the browser's per-origin beacon quota is
      // exhausted) — fall through to keepalive fetch in that case.
    }
  } catch {
    // fall through to fetch
  }
  try {
    const eventId = generateTraceId();
    const baseUrl: string = (typeof process !== 'undefined' && process.env?.REACT_APP_API_URL) || API_BASE;
    fetch(`${baseUrl}/api/telemetry/conversion`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        conversion_type: conversionType,
        platform,
        event_id: eventId,
        fired_client_side: firedClientSide,
        metadata: { ...metadata, correlation: getTelemetryCorrelation() },
      }),
      keepalive: true,
      credentials: 'include',
    }).catch(() => {});
  } catch {
    // best-effort: never throw from telemetry
  }
};

/**
 * Fire a Google Ads conversion event, with a durable server-side backup
 * record (see postAdConversionRecord) so the conversion is never silently
 * lost to an ad-blocker or a tab closing mid-call.
 *
 * Safe to call even when Google Ads tracking is not configured client-side
 * — the durable record still fires (so ops has full signal on how many
 * conversions are HAPPENING even before real ad-platform IDs are wired up),
 * but the actual gtag() call is skipped when unconfigured.
 */
export const trackConversion = (conversionLabel: string, value?: number): void => {
  const adsId = process.env.REACT_APP_GOOGLE_ADS_ID;
  const canFireClientSide = Boolean(adsId) && typeof window !== 'undefined' && typeof window.gtag === 'function';
  if (canFireClientSide) {
    window.gtag!('event', 'conversion', {
      send_to: `${adsId}/${conversionLabel}`,
      ...(value !== undefined ? { value, currency: 'USD' } : {}),
    });
  }
  postAdConversionRecord('signup', 'google_ads', canFireClientSide, { conversion_label: conversionLabel, value });
};

/**
 * Fire a named Reddit Pixel conversion event (e.g. 'SignUp', 'Purchase'),
 * with the same durable server-side backup as trackConversion. Safe to call
 * even when the Reddit Pixel isn't configured for this product.
 *
 * NOT wired into any conversion points yet on msapling.com (no product-specific
 * Reddit pixel ID configured here today — see initRedditPixel's doc comment);
 * exported for future use.
 */
export const trackRedditConversion = (eventName: string, metadata?: Record<string, unknown>): void => {
  const pixelId = process.env.REACT_APP_REDDIT_PIXEL_ID;
  const canFireClientSide = Boolean(pixelId) && typeof window !== 'undefined' && typeof window.rdt === 'function';
  if (canFireClientSide) {
    (window.rdt as (...args: unknown[]) => void)('track', eventName);
  }
  postAdConversionRecord(eventName.toLowerCase(), 'reddit', canFireClientSide, metadata);
};

/**
 * Reddit Pixel loader (standard `rdt('init', ...)` snippet). Gated on
 * REACT_APP_REDDIT_PIXEL_ID. A complete no-op when unset.
 *
 * IMPORTANT: do not hardcode a default pixel ID here — the only real pixel
 * ID currently available (a2_ioawm5d4plrd) belongs to a different product
 * (play.mtreeai.com) and would misattribute conversions if used on
 * msapling.com. This must always come from msapling.com's own future env
 * var value.
 */
export const initRedditPixel = (): void => {
  const pixelId = process.env.REACT_APP_REDDIT_PIXEL_ID;
  if (!pixelId || typeof window === 'undefined') return;

  if (typeof window.rdt !== 'function') {
    const callQueue: unknown[][] = [];
    const rdt = ((...args: unknown[]) => {
      callQueue.push(args);
    }) as ((...args: unknown[]) => void) & { callQueue?: unknown[] };
    rdt.callQueue = callQueue;
    window.rdt = rdt;
  }
  const rdtCall = window.rdt as (...args: unknown[]) => void;
  rdtCall('init', pixelId);
  rdtCall('track', 'PageVisit');

  injectScript('https://www.redditstatic.com/ads/pixel.js');
};

/**
 * Initializes all ad-conversion trackers. Each individual loader is
 * independently env-gated and no-ops on its own, so calling this is always
 * safe regardless of which (if any) env vars are set.
 */
export const initAdTracking = (): void => {
  initGA4();
  initGoogleAds();
  initRedditPixel();
};
